import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles, type Article } from '@/mocks/articles';

const ITEMS_PER_PAGE = 6;

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles: Article[] = useMemo(() => {
    if (!slug) return [];
    return articles.filter((a) => a.categorySlug === slug);
  }, [slug]);

  const totalPages = Math.ceil(categoryArticles.length / ITEMS_PER_PAGE);
  const paginatedArticles = categoryArticles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 黑料社区`
    : '分类浏览 - 黑料社区';
  const pageDesc = currentCategory
    ? `${currentCategory.description} - 黑料社区为您提供${currentCategory.name}相关的最新内容，共${currentCategory.articleCount}篇。`
    : '浏览黑料社区所有内容分类，包括最新爆料、社会热点、娱乐八卦、行业揭秘、深度调查和网友投稿。';

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`黑料社区,${currentCategory?.name || '分类'},真实内容,社区讨论`}
        canonicalUrl={`https://heiliaoshequ.com/category/${slug || ''}`}
      />

      <main>
        <Navbar />
        <div className="pt-24 md:pt-28 pb-14 md:pb-20">
          <div className="container-custom">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: currentCategory?.name || '分类浏览' },
                ]}
              />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {currentCategory?.name || '分类浏览'}
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              {currentCategory?.description || '浏览所有内容分类'}
            </p>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {paginatedArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {paginatedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <div className="h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-400">·</span>
                        <span className="text-xs text-foreground-400">{article.author}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-1-line"></i> {article.commentCount.toLocaleString()}
                        </span>
                        <div className="flex flex-wrap gap-1 ml-auto">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无内容</p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      currentPage === page
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}